import { v4 as uuidv4 } from 'uuid';
import getApiConfig from '../config/apiConfig';
import { logAPIError, formatAPIError } from '../utils/apiErrorLogger';

// Helper function to transform PascalCase to camelCase
const transformToCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  
  const transformed = {};
  Object.keys(obj).forEach(key => {
    let camelKey = key.charAt(0).toLowerCase() + key.slice(1);
    
    // Special handling for Id field - map to companyId for consistency
    if (key === 'Id') {
      camelKey = 'companyId';
    }
    
    transformed[camelKey] = obj[key];
  });
  return transformed;
};

const createEmptyCompany = () => ({
  companyId: '',
  companyName: '',
  website: '',
  street1: '',
  street2: '',
  city: '',
  province: '',
  country: '',
  postalCode: '',
  email: '',
  mainPhone: '',
  secondaryPhone: '',
  description: '',
  notes: '',
  industry: '',
  linkedInProfile: '',
  createdByEmail: '',
  createdDate: '',
  modifiedByEmail: null,
  modifiedDate: null
});

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error);
  }
  const data = await response.json();
  return Array.isArray(data) ? data.map(transformToCamelCase) : transformToCamelCase(data);
};

export const companyService = {
  createEmptyCompany,
  
  validateConfiguration() {
    return true;
  },

  // Get all companies
  async getCompanies() {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/companies`);
      return handleResponse(response);
    } catch (error) {
      logAPIError('companyService', 'getCompanies', 'GET', error);
      throw error;
    }
  },

  // Get company by ID
  async getCompany(companyId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/companies/${companyId}`);
      return handleResponse(response);
    } catch (error) {
      logAPIError('companyService', 'getCompany', 'GET', error, { companyId });
      throw error;
    }
  },

  // Save company (create or update)
  async saveCompany(company, currentUserEmail) {
    const isNew = !company.companyId;
    
    try {
      const { apiBaseUrl } = getApiConfig();
      
      if (isNew) {
        company.companyId = uuidv4();
        company.createdByEmail = currentUserEmail;
        company.createdDate = new Date().toISOString();
      }

      // Transform to PascalCase for API
      const apiCompany = {
        Id: company.companyId,
        CompanyName: company.companyName,
        Website: company.website,
        Street1: company.street1,
        Street2: company.street2,
        City: company.city,
        Province: company.province,
        Country: company.country,
        PostalCode: company.postalCode,
        Email: company.email,
        MainPhone: company.mainPhone,
        SecondaryPhone: company.secondaryPhone,
        Description: company.description,
        Notes: company.notes,
        Industry: company.industry,
        LinkedInProfile: company.linkedInProfile,
        CreatedByEmail: company.createdByEmail,
        CreatedDate: company.createdDate,
        ModifiedByEmail: company.modifiedByEmail,
        ModifiedDate: company.modifiedDate
      };

      const method = isNew ? 'POST' : 'PUT';
      const url = isNew 
        ? `${apiBaseUrl}/companies`
        : `${apiBaseUrl}/companies/${company.companyId}`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiCompany)
      });

      return handleResponse(response);
    } catch (error) {
      logAPIError('companyService', 'saveCompany', isNew ? 'POST' : 'PUT', error, {
        companyId: company.companyId,
        currentUserEmail
      });
      throw error;
    }
  },

  // Delete company
  async deleteCompany(companyId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/companies/${companyId}`, {
        method: 'DELETE'
      });
      return handleResponse(response);
    } catch (error) {
      logAPIError('companyService', 'deleteCompany', 'DELETE', error, { companyId });
      throw error;
    }
  },

  // Get company contacts
  async getCompanyContacts(companyId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/companies/${companyId}/contacts`);
      return handleResponse(response);
    } catch (error) {
      logAPIError('companyService', 'getCompanyContacts', 'GET', error, { companyId });
      throw error;
    }
  },

  // Get company opportunities
  async getCompanyOpportunities(companyId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/companies/${companyId}/opportunities`);
      return handleResponse(response);
    } catch (error) {
      logAPIError('companyService', 'getCompanyOpportunities', 'GET', error, { companyId });
      throw error;
    }
  }
};